'use client';

import { c as _c } from "react/compiler-runtime";
import { type ConvictionChartSnapshotInput, convictionChartTerminalTo, type GetMarketRow, resolveConvictionChartStart, selectConvictionChartInterval, useAuthoritativeConvictionChart } from '@morfi/backend-sdk-v2/client';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getAvailableIntervals, resolveAvailableInterval, windowSecondsForTimeframe } from '@/lib/chart/chart-factory';
import { resolveTerminalChartLifecycle } from '@/lib/chart/terminal-chart-lifecycle';
import { logger } from '@/lib/telemetry/logger';
import type { ChartIntentStore, MarketChartIntentSelection } from '@/stores/chart-intent';

/** A cached route can contain an older preference; intent warms the current SDK identity. */
export function ChartIntentConviction(t0) {
  const $ = _c(44);
  const {
    selection,
    store,
    market
  } = t0;
  let anchorMinuteMs;
  let from;
  let head;
  let interval;
  let t1;
  let t2;
  let t3;
  let t4;
  let to;
  if ($[0] !== market.createdAt || $[1] !== market.pubkey || $[2] !== market.resolvedAt || $[3] !== market.status || $[4] !== market.tradingStartedAt || $[5] !== selection.interval || $[6] !== selection.renderMode || $[7] !== selection.startedAt) {
    const lifecycle = resolveTerminalChartLifecycle({
      status: market.status,
      resolvedAt: market.resolvedAt,
      persistedInterval: selection.interval,
      persistedRenderMode: selection.renderMode,
      readOnly: false
    });
    anchorMinuteMs = lifecycle.resolutionUnix === undefined ? Math.floor(selection.startedAt / 60000) * 60000 : lifecycle.resolutionUnix * 1000;
    head = new Date(anchorMinuteMs).toISOString();
    const tradingStartedAt = market.tradingStartedAt ?? market.createdAt;
    const lifespanSeconds = Math.max(0, anchorMinuteMs / 1000 - Date.parse(tradingStartedAt) / 1000);
    interval = resolveAvailableInterval(lifecycle.interval, getAvailableIntervals(lifespanSeconds));
    from = resolveConvictionChartStart({
      tradingStartedAt,
      head,
      ...(interval === "all" ? {} : {
        lookbackSeconds: windowSecondsForTimeframe(interval, lifespanSeconds)
      })
    });
    to = lifecycle.resolutionUnix === undefined ? undefined : convictionChartTerminalTo(head);
    t4 = useAuthoritativeConvictionChart;
    t1 = market.pubkey;
    t2 = interval;
    t3 = selectConvictionChartInterval(from, to ?? new Date(anchorMinuteMs + 1).toISOString(), 180);
    $[0] = market.createdAt;
    $[1] = market.pubkey;
    $[2] = market.resolvedAt;
    $[3] = market.status;
    $[4] = market.tradingStartedAt;
    $[5] = selection.interval;
    $[6] = selection.renderMode;
    $[7] = selection.startedAt;
    $[8] = anchorMinuteMs;
    $[9] = from;
    $[10] = head;
    $[11] = interval;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = to;
  } else {
    anchorMinuteMs = $[8];
    from = $[9];
    head = $[10];
    interval = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    to = $[16];
  }
  let t5;
  if ($[17] !== to) {
    t5 = to === undefined ? {} : {
      to
    };
    $[17] = to;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== from || $[20] !== head || $[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t5) {
    t6 = {
      pubkey: t1,
      selectionKey: t2,
      interval: t3,
      from,
      head,
      limit: 180,
      ...t5
    };
    $[19] = from;
    $[20] = head;
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const query = t4(t6);
  const cachedError = useRef(query.error);
  const snapshot = useRef(query.data);
  let t7;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => snapshot.current;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const [readSnapshot] = useState(t7);
  let t8;
  let t9;
  if ($[27] !== anchorMinuteMs || $[28] !== interval || $[29] !== query.data || $[30] !== readSnapshot || $[31] !== selection || $[32] !== store) {
    t8 = () => {
      snapshot.current = query.data;
      if (query.data !== undefined) {
        store.getState().registerConviction(selection, interval, anchorMinuteMs, readSnapshot);
      }
    };
    t9 = [store, selection, interval, anchorMinuteMs, readSnapshot, query.data];
    $[27] = anchorMinuteMs;
    $[28] = interval;
    $[29] = query.data;
    $[30] = readSnapshot;
    $[31] = selection;
    $[32] = store;
    $[33] = t8;
    $[34] = t9;
  } else {
    t8 = $[33];
    t9 = $[34];
  }
  useLayoutEffect(t8, t9);
  let t10;
  let t11;
  if ($[35] !== interval || $[36] !== query.data || $[37] !== query.error || $[38] !== query.isFetching || $[39] !== selection.key || $[40] !== selection.marketPubkey || $[41] !== store) {
    t10 = () => {
      if (query.error === null || query.error === cachedError.current || query.isFetching || query.data !== undefined) {
        return;
      }
      logger.warn("chart.intent_snapshot_failed", {
        marketPubkey: selection.marketPubkey,
        property: "twar",
        interval
      });
      store.getState().clear(selection.key);
    };
    t11 = [query.error, query.isFetching, query.data, store, selection.key, selection.marketPubkey, interval];
    $[35] = interval;
    $[36] = query.data;
    $[37] = query.error;
    $[38] = query.isFetching;
    $[39] = selection.key;
    $[40] = selection.marketPubkey;
    $[41] = store;
    $[42] = t10;
    $[43] = t11;
  } else {
    t10 = $[42];
    t11 = $[43];
  }
  useEffect(t10, t11);
  return null;
}